/**
 * Business Pipeline stages, phases, and engagement model.
 *
 * Two orthogonal axes describe a talent's position on the platform:
 *
 *  • Phase (mutex): `pending_invite | vetting | sponsored | archived`
 *     — derived from `journey_instances.pipeline_stage` + `deleted_at`.
 *     A journey is in exactly ONE phase at a time. Drives the Pipeline
 *     kanban buckets and the cohort/program overview's "Vetting vs
 *     Sponsored" grouping.
 *
 *  • Engagement (parallel): `mobilityActive | adaptationActive | careActive`
 *     — derived from each hero's own state tables (TelePortal,
 *     QultureUp, Care). Only meaningful when `phase === 'sponsored'`.
 *     A sponsored talent can be in 0, 1, 2, or all 3 engagements at
 *     the same time. This reflects the platform's actual behaviour:
 *     once sponsorship activates, the citizen simultaneously works
 *     visa logistics, cultural missions, and well-being check-ins.
 *
 * Use `derivePhase()` everywhere that asks "what phase is this journey
 * in?" — never re-implement the mapping locally.
 */

// ─── Canonical journey_instances.pipeline_stage values ─────────────────
// Mirrors the Postgres enum declared in
// `packages/database/src/schema/journey.ts:102` (`journeyPipelineStageEnum`).
// Keep the union in sync if the enum changes.

export const SPONSORED_SUBSTAGES = ['mobility', 'integration', 'care', 'evidence'] as const;
export type SponsoredSubstage = (typeof SPONSORED_SUBSTAGES)[number];

// ─── Phase axis (mutex) ────────────────────────────────────────────────

export type Phase = 'pending_invite' | 'vetting' | 'sponsored' | 'archived';

/**
 * Map a `journey_instances` row's `pipeline_stage` + `deleted_at` to its
 * canonical lifecycle Phase. Single source of truth for the
 * "Vetting vs Sponsored vs …" classification used across the Business
 * portal (cohort detail, program detail, pipeline kanban, dashboard).
 *
 * Rules:
 *   • soft-deleted (`deletedAt != null`)            → 'archived'
 *   • null / 'acquisition' (pending invite, not yet
 *     accepted by the citizen)                      → 'pending_invite'
 *   • 'vetting' (accepted, in CheckUp)              → 'vetting'
 *   • SPONSORED_SUBSTAGES (mobility, integration,
 *     care, evidence — all post-sponsorship)        → 'sponsored'
 *   • any other / unknown value                     → 'pending_invite'
 *     (defensive default; should never happen in
 *     practice — the enum is closed)
 */
export function derivePhase(
  pipelineStage: string | null | undefined,
  deletedAt: Date | string | null | undefined
): Phase {
  if (deletedAt) return 'archived';
  if (!pipelineStage || pipelineStage === 'acquisition') return 'pending_invite';
  if (pipelineStage === 'vetting') return 'vetting';
  if ((SPONSORED_SUBSTAGES as readonly string[]).includes(pipelineStage)) return 'sponsored';
  return 'pending_invite';
}

/**
 * Convenience: is this journey in the sponsored phase? Equivalent to
 * `derivePhase(stage, deletedAt) === 'sponsored'`, but cheaper when the
 * caller already knows the journey isn't archived.
 */
export function isSponsoredStage(pipelineStage: string | null | undefined): boolean {
  return !!pipelineStage && (SPONSORED_SUBSTAGES as readonly string[]).includes(pipelineStage);
}

// ─── Legacy stage labels (kept for back-compat with older surfaces) ────
// These names do NOT match the live DB enum
// ('acquisition | vetting | mobility | integration | care | evidence');
// they predate Cornerstone v2. New code should use the Phase model
// above. Do not extend this object.
//
// TODO(housekeeping): remove once no remaining callers reference
// PIPELINE_STAGES.* directly.

export const PIPELINE_STAGES = {
  VETTING: 'vetting',
  INTERVIEW: 'interview',
  OFFER: 'offer',
  ONBOARDING: 'onboarding',
  INTEGRATION: 'integration',
  COMPLETED: 'completed',
} as const;

export type PipelineStage = (typeof PIPELINE_STAGES)[keyof typeof PIPELINE_STAGES];

export const PIPELINE_STAGE_ORDER: PipelineStage[] = [
  PIPELINE_STAGES.VETTING,
  PIPELINE_STAGES.INTERVIEW,
  PIPELINE_STAGES.OFFER,
  PIPELINE_STAGES.ONBOARDING,
  PIPELINE_STAGES.INTEGRATION,
  PIPELINE_STAGES.COMPLETED,
];

export const PIPELINE_STAGE_LABELS: Record<PipelineStage, string> = {
  vetting: 'Vetting',
  interview: 'Interview',
  offer: 'Offer',
  onboarding: 'Onboarding',
  integration: 'Integration',
  completed: 'Completed',
};

export const PIPELINE_STAGE_COLORS: Record<PipelineStage, string> = {
  vetting: '#94a3b8',
  interview: '#60a5fa',
  offer: '#fbbf24',
  onboarding: '#a78bfa',
  integration: '#34d399',
  completed: '#22c55e',
};
